// ============================================
// CHÁMOMILE - Customer Review Page
// Version 2.1 - FIXED: Dynamic discount display (5% or 10%)
// ============================================
// 
// URL Formats:
// - Secure: /review?token=ABC123XYZ (single-use, recommended)
// - Legacy: /review?order=ONL-0001&phone=0123456789 (backward compatible)
//
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage, isFirebaseConfigured } from '../config/firebase';
import {
  validateReviewToken,
  validateLegacyReviewAccess,
  submitReview,
  REVIEW_REWARD_ENABLED,
} from '../services/reviewService';
import { fetchOnlineProducts } from '../services/productService';

// Premium Gold Color Palette
const colors = {
  gold50: '#faf8f3', gold100: '#f5f0e6', gold200: '#ebe0cc', gold300: '#dccaa6',
  gold400: '#c9a962', gold500: '#b8943d', gold600: '#a07d2e', gold700: '#846628',
  stone50: '#fafaf9', stone100: '#f5f5f4', stone200: '#e7e5e4', stone300: '#d6d3d1',
  stone400: '#a8a29e', stone500: '#78716c', stone600: '#57534e', stone700: '#44403c',
  stone800: '#292524', stone900: '#1c1917',
  success: '#22c55e', successLight: '#dcfce7', error: '#ef4444', errorLight: '#fee2e2',
};

// Fallback product data (used when Firestore is unavailable)
const fallbackProducts = [
  { id: '1', name: 'Lavender Lullaby', imageUrl: 'https://i.imgur.com/oSJtYcs.png', price: 13 },
  { id: '2', name: 'Gentle Chamomile', imageUrl: 'https://i.imgur.com/xi0bRZT.png', price: 13 },
  { id: '3', name: 'Lychee Rosette', imageUrl: 'https://i.imgur.com/zamM3wD.png', price: 13 },
  { id: '4', name: 'Sun-Kissed Peach', imageUrl: 'https://i.imgur.com/H12UWmK.png', price: 13 },
  { id: '5', name: 'Moonlit Jasmine', imageUrl: 'https://i.imgur.com/iZhx5QO.png', price: 13 },
  { id: '6', name: 'Blushing Berry', imageUrl: 'https://i.imgur.com/PCW1EOn.png', price: 14 },
];

// Icons
const Icons = {
  Star: ({ size = 24, filled = false }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill={filled ? '#f59e0b' : 'none'}
      stroke={filled ? '#f59e0b' : '#d1d5db'}
      strokeWidth="1.5">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  Camera: ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </svg>
  ),
  Check: ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  X: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" x2="6" y1="6" y2="18" />
      <line x1="6" x2="18" y1="6" y2="18" />
    </svg>
  ),
  Gift: ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13" />
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5" />
    </svg>
  ),
  Copy: ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect width="14" height="14" x="8" y="8" rx="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  ),
  AlertCircle: ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  ),
  Lock: ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect width="18" height="11" x="3" y="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
};

// Styles
const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(180deg, #fff 0%, ${colors.stone50} 50%, #fff 100%)`,
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
    color: colors.stone800,
    paddingBottom: '80px', // Extra for iOS Safari nav bar
  },
  header: {
    textAlign: 'center',
    padding: '20px 16px',
    borderBottom: `1px solid ${colors.stone100}`,
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(20px)',
  },
  logo: {
    fontSize: '24px',
    fontWeight: 700,
    color: colors.stone900,
    fontFamily: 'Georgia, serif',
    margin: 0,
  },
  container: {
    maxWidth: '500px',
    margin: '0 auto',
    padding: '24px 16px',
  },
  card: {
    background: '#fff',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: `1px solid ${colors.stone100}`,
    marginBottom: '20px',
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '16px 28px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#fff',
    background: `linear-gradient(135deg, ${colors.gold400} 0%, ${colors.gold500} 100%)`,
    border: 'none',
    borderRadius: '999px',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(201,169,98,0.3)',
    transition: 'all 0.25s ease',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '14px',
    border: `2px solid ${colors.stone200}`,
    borderRadius: '12px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s ease',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.stone500,
    marginBottom: '8px',
  },
};

// Image compression utility
const compressImage = async (file, maxSize = 800, quality = 0.7) => {
  if (file.size < 300 * 1024) return file;

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }));
          } else {
            resolve(file);
          }
        }, 'image/jpeg', quality);
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

// Interactive Star Rating
const StarRating = ({ rating, onRatingChange, size = 32 }) => {
  const [hoverRating, setHoverRating] = useState(0);

  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          onClick={() => onRatingChange(star)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            transition: 'transform 0.15s ease',
            transform: star <= (hoverRating || rating) ? 'scale(1.2)' : 'scale(1)',
          }}
        >
          <Icons.Star size={size} filled={star <= (hoverRating || rating)} />
        </button>
      ))}
    </div>
  );
};

// Collapsible Flavor Review Card for Per-Flavor Reviews
const FlavorReviewCard = ({ product, review, onUpdate, onPhotoSelect }) => {
  const { rating, title, comment, photoPreview, expanded } = review;

  const toggleExpand = () => {
    onUpdate({ ...review, expanded: !expanded });
  };

  const hasReview = rating > 0 || title || comment;

  return (
    <div style={{
      border: `2px solid ${expanded ? colors.gold400 : colors.stone200}`,
      borderRadius: '16px',
      marginBottom: '12px',
      background: expanded ? colors.gold50 : '#fff',
      transition: 'all 0.2s ease',
      overflow: 'hidden',
    }}>
      {/* Header - Always Visible */}
      <div
        onClick={toggleExpand}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '16px',
          cursor: 'pointer',
          gap: '12px',
        }}
      >
        <img
          src={product.imageUrl}
          alt={product.name}
          style={{ width: '48px', height: '48px', objectFit: 'contain' }}
        />
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '14px', fontWeight: 600, margin: 0, color: colors.stone800 }}>
            {product.name}
          </p>
          {hasReview && !expanded && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
              {[...Array(5)].map((_, i) => (
                <Icons.Star key={i} size={12} filled={i < rating} />
              ))}
              <span style={{ fontSize: '11px', color: colors.stone500, marginLeft: '4px' }}>
                Review added
              </span>
            </div>
          )}
          {!hasReview && !expanded && (
            <p style={{ fontSize: '12px', color: colors.stone400, margin: '4px 0 0' }}>
              Tap to add review (optional)
            </p>
          )}
        </div>
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          background: colors.stone100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s ease',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${colors.stone200}` }}>
          {/* Rating */}
          <div style={{ paddingTop: '16px', marginBottom: '16px' }}>
            <label style={styles.label}>Rating</label>
            <StarRating
              rating={rating}
              onRatingChange={(r) => onUpdate({ ...review, rating: r })}
              size={28}
            />
          </div>

          {/* Title */}
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.label}>Review Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => onUpdate({ ...review, title: e.target.value })}
              placeholder="Sum it up in a few words"
              style={styles.input}
            />
          </div>

          {/* Comment */}
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.label}>Your Review</label>
            <textarea
              value={comment}
              onChange={(e) => onUpdate({ ...review, comment: e.target.value })}
              placeholder="Share your experience..."
              rows={3}
              style={{ ...styles.input, resize: 'vertical', minHeight: '80px' }}
            />
          </div>

          {/* Photo */}
          <div>
            <label style={styles.label}>Add a Photo (optional)</label>
            {photoPreview ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={photoPreview}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '12px' }}
                />
                <button
                  onClick={() => onUpdate({ ...review, photo: null, photoPreview: null })}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'rgba(0,0,0,0.6)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                  }}
                >
                  <Icons.X size={12} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => onPhotoSelect(product.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: colors.stone100,
                  border: `1px dashed ${colors.stone300}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  color: colors.stone600,
                  fontSize: '13px',
                }}
              >
                <Icons.Camera size={18} />
                Add Photo
              </button>
            )}
          </div>

          {/* Skip Button */}
          {!hasReview && (
            <button
              onClick={toggleExpand}
              style={{
                width: '100%',
                padding: '10px',
                marginTop: '16px',
                background: 'transparent',
                border: 'none',
                color: colors.stone400,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Skip this flavor
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default function ReviewPage() {
  // Dynamic product list from Firestore
  const [products, setProducts] = useState(fallbackProducts);

  // State
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [reviewToken, setReviewToken] = useState('');
  const [purchasedItems, setPurchasedItems] = useState([]); // Items from the order

  // Form state - now per-flavor
  const [selectedProduct, setSelectedProduct] = useState(null); // For non-purchased review
  const [flavorReviews, setFlavorReviews] = useState({}); // { productId: { rating, title, comment, photo, expanded } }
  const [rating, setRating] = useState(0);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  // Success state
  const [submitted, setSubmitted] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [copied, setCopied] = useState(false);

  const fileInputRef = useRef(null);

  // Load products from Firestore on mount
  useEffect(() => {
    fetchOnlineProducts()
      .then(p => { if (p && p.length > 0) setProducts(p); })
      .catch(() => { /* keep fallback */ });
  }, []);

  // Validate access on mount
  useEffect(() => {
    validateAccess();
  }, []);

  const validateAccess = async () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const orderParam = params.get('order');
    const phoneParam = params.get('phone');

    // Try token-based validation first (preferred)
    if (token) {
      const result = await validateReviewToken(token);
      if (result.valid) {
        setIsVerified(true);
        setOrderNumber(result.orderNumber || '');
        setCustomerName(result.customerName || '');
        setCustomerPhone(result.customerPhone || '');
        setName(result.customerName || '');
        setReviewToken(token);
        // Store purchased items for per-flavor reviews
        if (result.purchasedItems && result.purchasedItems.length > 0) {
          console.log('🛒 ReviewPage received purchasedItems:', result.purchasedItems);
          setPurchasedItems(result.purchasedItems);
          // Initialize flavor reviews state for each purchased item
          const initialReviews = {};
          result.purchasedItems.forEach(item => {
            initialReviews[item.id] = { rating: 0, title: '', comment: '', photo: null, photoPreview: null, expanded: false };
          });
          setFlavorReviews(initialReviews);
        } else {
          console.log('⚠️ ReviewPage: No purchasedItems in token result', result);
        }
        setLoading(false);
        return;
      } else {
        setAccessError(result.error || 'Invalid review link');
        setLoading(false);
        return;
      }
    }

    // Fallback to legacy validation
    if (orderParam && phoneParam) {
      const result = await validateLegacyReviewAccess(orderParam, phoneParam);
      if (result.valid) {
        setIsVerified(true);
        setOrderNumber(result.orderNumber || orderParam);
        setCustomerName(result.customerName || '');
        setCustomerPhone(phoneParam);
        setName(result.customerName || '');
        setLoading(false);
        return;
      } else {
        setAccessError(result.error || 'Invalid access');
        setLoading(false);
        return;
      }
    }

    // No validation params - allow anonymous review
    setIsVerified(false);
    setLoading(false);
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be under 10MB');
      return;
    }

    setUploading(true);
    setUploadProgress('Processing image...');

    try {
      const compressed = await compressImage(file, 800, 0.7);
      setPhotoFile(compressed);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setUploading(false);
        setUploadProgress('');
      };
      reader.readAsDataURL(compressed);
    } catch (error) {
      console.error('Error processing image:', error);
      setUploading(false);
      setUploadProgress('');
      alert('Error processing image. Please try again.');
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    // Per-Flavor Review Mode
    if (purchasedItems.length > 0) {
      // Check if at least one flavor has a review
      const reviewsToSubmit = Object.entries(flavorReviews).filter(
        ([_, review]) => review.rating > 0 && review.comment?.trim()
      );

      if (reviewsToSubmit.length === 0) {
        alert('Please add at least one review for your purchased items');
        return;
      }

      if (!name.trim()) {
        alert('Please enter your name');
        return;
      }

      setUploading(true);
      setUploadProgress('Submitting reviews...');

      try {
        let bestDiscount = 0;
        let earnedCode = '';

        for (const [productId, review] of reviewsToSubmit) {
          const product = products.find(p => p.id === productId) || { id: productId, name: purchasedItems.find(i => i.id === productId)?.name || 'Unknown' };
          let imageUrl = null;

          // Upload photo if provided
          if (review.photo && isFirebaseConfigured() && storage) {
            setUploadProgress(`Uploading photo for ${product.name}...`);
            const timestamp = Date.now();
            const filename = `review_${productId}_${timestamp}.jpg`;
            const storageRef = ref(storage, `reviews/${filename}`);
            await uploadBytes(storageRef, review.photo);
            imageUrl = await getDownloadURL(storageRef);
          }

          setUploadProgress(`Saving review for ${product.name}...`);

          const result = await submitReview({
            productId: product.id,
            productName: product.name,
            customerName: name.trim(),
            customerPhone: customerPhone || undefined,
            rating: review.rating,
            title: review.title?.trim() || undefined,
            comment: review.comment.trim(),
            orderId: orderNumber || undefined,
            imageUrl,
            token: reviewToken || undefined,
          });

          if (result.success && result.discountCode) {
            // Track best discount (photo review = 10%, no photo = 5%)
            const thisDiscount = imageUrl ? 10 : 5;
            if (thisDiscount > bestDiscount) {
              bestDiscount = thisDiscount;
              earnedCode = result.discountCode;
            }
          }
        }

        // Show success with best discount earned
        setDiscountPercent(bestDiscount);
        setDiscountCode(earnedCode);
        setSubmitted(true);
      } catch (error) {
        console.error('Error submitting reviews:', error);
        alert('Error submitting reviews. Please try again.');
      } finally {
        setUploading(false);
        setUploadProgress('');
      }
      return;
    }

    // Traditional Single Review Mode
    if (!selectedProduct || rating === 0 || !comment.trim() || !name.trim() || !title.trim()) {
      alert('Please complete all required fields (product, rating, name, title, and review)');
      return;
    }

    setUploading(true);
    setUploadProgress('Submitting review...');

    try {
      let imageUrl = null;

      // Upload photo if provided
      if (photoFile && isFirebaseConfigured() && storage) {
        setUploadProgress('Uploading photo...');
        const timestamp = Date.now();
        const filename = `review_${timestamp}.jpg`;
        const storageRef = ref(storage, `reviews/${filename}`);
        await uploadBytes(storageRef, photoFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      setUploadProgress('Saving review...');

      const result = await submitReview({
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        customerName: name.trim(),
        customerPhone: customerPhone || undefined,
        rating,
        title: title.trim() || undefined,
        comment: comment.trim(),
        orderId: orderNumber || undefined,
        imageUrl,
        token: reviewToken || undefined,
      });

      if (result.success) {
        const hasPhoto = !!imageUrl;
        const earnedDiscount = hasPhoto ? 10 : 5;
        setDiscountPercent(earnedDiscount);
        setDiscountCode(result.discountCode || '');
        setSubmitted(true);
      } else {
        alert(result.error || 'Failed to submit review');
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Error submitting review. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(discountCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <h1 style={styles.logo}>Chámomile</h1>
        </header>
        <div style={styles.container}>
          <div style={{ ...styles.card, textAlign: 'center', padding: '48px 24px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: `3px solid ${colors.stone200}`,
              borderTopColor: colors.gold500,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.stone500 }}>Validating your review link...</p>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Access error state
  if (accessError) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <h1 style={styles.logo}>Chámomile</h1>
        </header>
        <div style={styles.container}>
          <div style={{ ...styles.card, textAlign: 'center', padding: '32px 24px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: colors.errorLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: colors.error,
            }}>
              <Icons.AlertCircle size={32} />
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '12px' }}>
              Unable to Access Review
            </h2>
            <p style={{ color: colors.stone500, fontSize: '14px', marginBottom: '24px', lineHeight: 1.6 }}>
              {accessError}
            </p>

            {accessError.includes('already been used') && (
              <div style={{
                background: colors.gold50,
                border: `1px solid ${colors.gold200}`,
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px',
              }}>
                <p style={{ fontSize: '13px', color: colors.gold700, margin: 0 }}>
                  <strong>Thank you!</strong> Your review helps other customers discover their perfect blend.
                  Each review link can only be used once to ensure authentic feedback.
                </p>
              </div>
            )}

            <a
              href="https://chamomilemy.netlify.app"
              style={{
                ...styles.btnPrimary,
                textDecoration: 'none',
                display: 'inline-flex',
              }}
            >
              Visit Our Store
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Success state - review submitted
  if (submitted) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <h1 style={styles.logo}>Chámomile</h1>
        </header>
        <div style={{ ...styles.container, paddingBottom: 'max(32px, env(safe-area-inset-bottom) + 24px)' }}>
          <div style={{ ...styles.card, textAlign: 'center', padding: '32px 24px' }}>
            <div style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${colors.success}, #16a34a)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              color: '#fff',
              boxShadow: '0 8px 32px rgba(34,197,94,0.3)',
            }}>
              <Icons.Check size={36} />
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>
              Thank You!
            </h2>
            <p style={{ color: colors.stone500, marginBottom: '24px' }}>
              Your review has been submitted and is pending approval.
            </p>

            {/* Discount code for 4-5 star reviews */}
            {REVIEW_REWARD_ENABLED && discountCode && (
              <div style={{
                background: `linear-gradient(135deg, ${colors.gold50}, ${colors.gold100})`,
                border: `2px dashed ${colors.gold300}`,
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '24px',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                  color: colors.gold600,
                }}>
                  <Icons.Gift size={24} />
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>Your Reward</span>
                </div>
                {/* FIXED: Show dynamic discount percentage */}
                <p style={{ fontSize: '13px', color: colors.stone600, marginBottom: '12px' }}>
                  Here's {discountPercent}% off your next order!
                </p>
                <div style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '12px',
                }}>
                  <p style={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: colors.gold600,
                    fontFamily: 'monospace',
                    letterSpacing: '3px',
                    margin: 0,
                  }}>
                    {discountCode}
                  </p>
                </div>
                <button
                  onClick={copyCode}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '10px 20px',
                    background: colors.gold100,
                    border: 'none',
                    borderRadius: '999px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: colors.gold700,
                    cursor: 'pointer',
                  }}
                >
                  <Icons.Copy size={14} />
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
                <p style={{ fontSize: '11px', color: colors.stone400, marginTop: '12px' }}>
                  Valid for 30 days • Single use only
                </p>
              </div>
            )}

            <a
              href="https://chamomilemy.netlify.app"
              style={{
                ...styles.btnPrimary,
                textDecoration: 'none',
                display: 'block',
              }}
            >
              Order Again
            </a>
          </div>
        </div>

        {/* Order Again button moved outside card for better iOS visibility */}
        <div style={{
          padding: '0 16px 60px',
          maxWidth: '500px',
          margin: '0 auto',
        }}>
          {/* Safe area spacer for iOS */}
        </div>
      </div>
    );
  }

  // Helper function to get expected discount percentage
  const getExpectedDiscount = () => {
    if (rating >= 4) {
      return photoPreview ? 10 : 5;
    }
    return 0;
  };

  // Review form
  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.logo}>Chámomile</h1>
        <p style={{ fontSize: '10px', color: colors.stone400, letterSpacing: '2px', textTransform: 'uppercase', marginTop: '2px' }}>
          Share Your Experience
        </p>
      </header>

      <div style={styles.container}>
        {/* Verified badge */}
        {isVerified && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: colors.successLight,
            borderRadius: '999px',
            marginBottom: '20px',
          }}>
            <Icons.Check size={16} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: colors.success }}>
              Verified Purchase: {orderNumber}
            </span>
          </div>
        )}

        {/* Per-Flavor Reviews (when purchased items available) OR Product Selection (fallback) */}
        {purchasedItems.length > 0 ? (
          <div style={styles.card}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>
              Review Your Order
            </h3>
            <p style={{ fontSize: '13px', color: colors.stone500, marginBottom: '16px' }}>
              Tap each flavor to add a review (optional for each)
            </p>

            {purchasedItems.map(item => {
              const product = products.find(p => p.id === item.id) || { id: item.id, name: item.name, imageUrl: '' };
              return (
                <FlavorReviewCard
                  key={item.id}
                  product={product}
                  review={flavorReviews[item.id] || { rating: 0, title: '', comment: '', photo: null, photoPreview: null, expanded: false }}
                  onUpdate={(updatedReview) => setFlavorReviews(prev => ({ ...prev, [item.id]: updatedReview }))}
                  onPhotoSelect={(productId) => {
                    // Create a hidden file input and trigger it for this product
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const compressed = await compressImage(file, 800, 0.7);
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setFlavorReviews(prev => ({
                            ...prev,
                            [productId]: {
                              ...prev[productId],
                              photo: compressed,
                              photoPreview: reader.result
                            }
                          }));
                        };
                        reader.readAsDataURL(compressed);
                      }
                    };
                    input.click();
                  }}
                />
              );
            })}

            {/* Name Field for Per-Flavor Reviews */}
            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${colors.stone200}` }}>
              <label style={styles.label}>Your Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="How should we call you?"
                style={styles.input}
              />
            </div>
          </div>
        ) : (
          /* Step 1: Select Product (Fallback for anonymous reviews) */
          <div style={styles.card}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px' }}>
              1. Which blend did you try?
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
            }}>
              {products.map(product => (
                <div
                  key={product.id}
                  onClick={() => setSelectedProduct(product)}
                  style={{
                    padding: '12px 8px',
                    borderRadius: '12px',
                    border: `2px solid ${selectedProduct?.id === product.id ? colors.gold400 : colors.stone200}`,
                    background: selectedProduct?.id === product.id ? colors.gold50 : '#fff',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    style={{ width: '50px', height: '50px', objectFit: 'contain', marginBottom: '6px' }}
                  />
                  <p style={{ fontSize: '11px', fontWeight: 500, margin: 0, color: colors.stone700 }}>
                    {product.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Rating - Only for legacy single-review mode */}
        {purchasedItems.length === 0 && (
          <div style={styles.card}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px' }}>
              2. How would you rate it?
            </h3>
            <StarRating rating={rating} onRatingChange={setRating} />
            {rating > 0 && (
              <p style={{ textAlign: 'center', color: colors.gold600, fontSize: '14px', marginTop: '12px' }}>
                {rating === 5 ? '🌟 Excellent!' : rating === 4 ? '👍 Great!' : rating === 3 ? '😊 Good' : rating === 2 ? '😐 Fair' : '😕 Poor'}
              </p>
            )}
          </div>
        )}

        {/* Step 3: Review - Only for legacy single-review mode */}
        {purchasedItems.length === 0 && (
          <div style={styles.card}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px' }}>
              3. Tell us more
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>Your Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="How should we call you?"
                style={styles.input}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>Review Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Sum it up in a few words"
                style={styles.input}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>Your Review *</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your experience with this blend..."
                rows={4}
                style={{
                  ...styles.input,
                  resize: 'vertical',
                  minHeight: '100px',
                }}
              />
            </div>

            {/* Photo upload */}
            <div>
              <label style={styles.label}>
                Add a Photo (optional)
                {REVIEW_REWARD_ENABLED && rating >= 4 && !photoPreview && (
                  <span style={{ color: colors.gold600, fontWeight: 400 }}> — +5% extra discount!</span>
                )}
              </label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoSelect}
                accept="image/*"
                style={{ display: 'none' }}
              />

              {photoPreview ? (
                <div style={{
                  position: 'relative',
                  display: 'inline-block',
                }}>
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      borderRadius: '12px',
                    }}
                  />
                  <button
                    onClick={removePhoto}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.6)',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                    }}
                  >
                    <Icons.X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{
                    width: '100%',
                    padding: '20px',
                    border: `2px dashed ${colors.stone200}`,
                    borderRadius: '12px',
                    background: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'center',
                    color: colors.stone400,
                  }}
                >
                  <Icons.Camera size={24} />
                  <p style={{ margin: '8px 0 0', fontSize: '13px' }}>
                    {uploading ? uploadProgress : 'Tap to add photo'}
                  </p>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Submit Button - Dynamic based on mode */}
        {purchasedItems.length > 0 ? (
          <button
            onClick={handleSubmit}
            disabled={!name.trim() || uploading}
            style={{
              ...styles.btnPrimary,
              opacity: (!name.trim() || uploading) ? 0.5 : 1,
              cursor: (!name.trim() || uploading) ? 'not-allowed' : 'pointer',
            }}
          >
            {uploading ? uploadProgress : 'Submit Reviews'}
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!selectedProduct || rating === 0 || !comment.trim() || !name.trim() || uploading}
            style={{
              ...styles.btnPrimary,
              opacity: (!selectedProduct || rating === 0 || !comment.trim() || !name.trim() || uploading) ? 0.5 : 1,
              cursor: (!selectedProduct || rating === 0 || !comment.trim() || !name.trim() || uploading) ? 'not-allowed' : 'pointer',
            }}
          >
            {uploading ? uploadProgress : 'Submit Review'}
          </button>
        )}

        {/* FIXED: Dynamic discount message based on rating AND photo status */}
        {REVIEW_REWARD_ENABLED && rating >= 4 && (
          <p style={{
            textAlign: 'center',
            fontSize: '12px',
            color: colors.gold600,
            marginTop: '12px',
          }}>
            🎁 You'll receive a {getExpectedDiscount()}% discount code after submitting!
            {!photoPreview && rating === 5 && (
              <span style={{ display: 'block', marginTop: '4px', color: colors.stone500 }}>
                Add a photo to get 10% instead!
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
